import util from '../util';

export default class Widget {
	constructor({ container, api, socket, hash }) {
		this.container = container;
		this.api = api;
		this.socket = socket;
		this.hash = hash;

		if(!this.container.id) this.container.id = util.generateRandomString(16);

		this.settings = {};
		this.channels = [];
		this._channelFlagsMap = new Map(); // `${service_id}:${token}` → flags[]
		this._urlParams = Object.fromEntries(
			[...new URLSearchParams(window.location.search)].filter(([k]) => k !== 'hash')
		);
		this.eventTypes = {};
		this.giftTypes = {};
		this.giftThresholds = [];
		this.textReplacements = [];
		this.serviceIcons = {};
		this.userData = null;
		this._subId = null;
	}

	get _widgetType() { throw new Error('_widgetType not implemented'); }

	// ── Lifecycle ────────────────────────────────────────────────────────────

	async init() {
		this._build();
		await this.loadConfig();
		this._subId = this.socket.subscribe(this.hash, {
			...this._defaultSocketHandlers(),
			...this._socketHandlers(),
		});
	}

	// loadConfig: fetch and apply data needed at startup and on reconnect.
	// Base loads only settings. Subclasses extend for additional data.
	async loadConfig() {
		await this.loadSettings();
	}

	_channelHasFlag(serviceId, channelToken, flag) {
		return this._channelFlagsMap.get(`${serviceId}:${channelToken}`)?.includes(flag) || false;
	}

	destroy() {
		if(this._subId != null) {
			this.socket.unsubscribe(this._subId);
			this._subId = null;
		}
	}

	// ── Load methods — api call + apply; errors are caught and logged ────────

	async loadSettings() {
		try {
			const data = await this.api.get(`widget/${encodeURIComponent(this.hash)}/settings/${this._widgetType}`);
			this.applySettings(data);
		} catch(err) { console.error('loadSettings failed', err); }
	}

	async loadChannels() {
		try {
			this.channels = await this.api.get(`widget/${encodeURIComponent(this.hash)}/channels`);
			this._channelFlagsMap = new Map(this.channels.map(c => [`${c.service_id}:${c.token}`, c.flags || []]));
		} catch(e) {
			console.error('_refreshChannelFlags', e);
		}
	}

	async loadUserData() {
		try {
			const data = await this.api.get(`widget/${encodeURIComponent(this.hash)}/userData`);
			this.applyUserData(data);
		} catch(err) { console.error('loadUserData failed', err); }
	}

	async loadEventTypes() {
		try {
			const data = await this.api.get(`widget/${encodeURIComponent(this.hash)}/eventTypes`);
			this.applyEventTypes(data);
		} catch(err) { console.error('loadEventTypes failed', err); }
	}

	async loadGiftTypes() {
		try {
			const { results } = await this.api.get(`widget/${encodeURIComponent(this.hash)}/giftTypes`);
			this.applyGiftTypes(results);
		} catch(err) { console.error('loadGiftTypes failed', err); }
	}

	async loadGiftThresholds() {
		try {
			const data = await this.api.get(`widget/${encodeURIComponent(this.hash)}/giftThresholds`);
			this.applyGiftThresholds(data);
		} catch(err) { console.error('loadGiftThresholds failed', err); }
	}

	async loadTextReplacements() {
		try {
			const data = await this.api.get(`widget/${encodeURIComponent(this.hash)}/textReplacements`);
			this.applyTextReplacements(data);
		} catch(err) { console.error('loadTextReplacements failed', err); }
	}

	async loadServiceIcons() {
		try {
			const data = await this.api.get(`widget/${encodeURIComponent(this.hash)}/services`);
			this.applyServiceIcons(data);
		} catch(err) { console.error('loadServiceIcons failed', err); }
	}

	// ── Apply methods — call these from previews, socket events, tests ────────

	applySettings(settings) {
		const base = settings || {};
		if(Object.keys(this._urlParams).length) {
			const overrides = {};
			for(const [key, raw] of Object.entries(this._urlParams)) {
				if(!(key in base)) continue;
				const existing = base[key];
				if(typeof existing === 'boolean') overrides[key] = raw === 'true' || raw === '1';
				else if(typeof existing === 'number') overrides[key] = Number(raw);
				else if(existing === null || existing === undefined) {
					try { overrides[key] = JSON.parse(raw); } catch(e) { overrides[key] = raw; }
				} else overrides[key] = raw;
			}
			this.settings = { ...base, ...overrides };
		} else {
			this.settings = base;
		}
		this._generateCSS();
	}

	applyEventTypes(data) {
		this.eventTypes = {};
		for(const et of data) this.eventTypes[et.event_type] = et;
	}

	applyGiftTypes(results) {
		this.giftTypes = {};
		for(const gt of results) {
			if(!(gt.service_id in this.giftTypes)) this.giftTypes[gt.service_id] = {};
			this.giftTypes[gt.service_id][gt.gift_type] = gt;
		}
	}

	// Single-item variants for granular socket updates.
	applyGiftType(serviceId, giftType, data) {
		if(!(serviceId in this.giftTypes)) this.giftTypes[serviceId] = {};
		this.giftTypes[serviceId][giftType] = data;
	}

	applyEventType(eventType, data) {
		this.eventTypes[eventType] = data;
	}

	applyGiftThresholds(data) {
		this.giftThresholds = data;
	}

	applyTextReplacements(data) {
		const replacements = [];
		replacements.push({ expression: /^!.*/g, replacement: '' });
		if(data.replacements) {
			for(const r of data.replacements) {
				if(r.status !== 'enabled') continue;
				const flags = `g${r['case-sensitive'] ? '' : 'i'}`;
				const expression = r.regexp
					? new RegExp(r.replace, flags)
					: new RegExp(`${r.unbounded ? '' : '\\b'}${util.escapeRegExp(r.replace)}${r.unbounded ? '' : '\\b'}`, flags);
				replacements.push({ expression, replacement: r.with });
			}
		}
		replacements.push({ expression: /(((https?:\/\/)|(www\.))(\S+))/gi, replacement: '' });
		replacements.push({ expression: /((.+?)\2\2)\2+/gu, replacement: '$1' });
		replacements.push({ expression: /\s+/g, replacement: ' ' });
		this.textReplacements = replacements;
	}

	applyServiceIcons(data) {
		this.serviceIcons = Object.fromEntries(
			data.filter(s => s.icon?.url).map(s => [s.id, s.icon.url])
		);
	}

	applyUserData(data) {
		this.userData = data;
	}

	// ── CSS helpers ──────────────────────────────────────────────────────────

	_build() {}

	_generateCSS() {}

	_injectCSS(id, css) {
		if(document.getElementById(id)) return;
		const style = document.createElement('style');
		style.id = id;
		style.textContent = css;
		document.head.appendChild(style);
	}

	_setCSS(id, css) {
		let el = document.getElementById(id);
		if(!el) {
			el = document.createElement('style');
			el.id = id;
			document.head.appendChild(el);
		}
		el.textContent = css;
	}

	_updateFontLink() {
		document.getElementById('googleFontLink')?.remove();
		if(!this.settings.customFont && this.settings.fontFamily) {
			const link = document.createElement('link');
			link.rel = 'stylesheet';
			link.id = 'googleFontLink';
			link.href = `https://fonts.googleapis.com/css?family=${encodeURIComponent(this.settings.fontFamily)}`;
			document.head.appendChild(link);
		}
	}

	// ── Socket handlers ──────────────────────────────────────────────────────
	// _defaultSocketHandlers() handles the minimal common events.
	// Subclasses override _socketHandlers() to add their own.
	// Both are merged in init() — _socketHandlers() keys take precedence.

	_defaultSocketHandlers() {
		return {
			widgetSettingsChange: msg => {
				if(msg.widgetType !== this._widgetType) return;
				this.applySettings(msg.settings);
			},
			widgetSettingsFieldChange: data => {
				if(data.widgetType !== this._widgetType) return;
				this.applySettings({ ...this.settings, [data.field]: data.value });
			},
			reconnect: () => this.loadConfig(),
		};
	}

	_socketHandlers() {
		return {};
	}
}
